// 登入/註冊頁左側促銷面板（對齊設計稿：logo + 15 DAYS 免費試用 +
//   BUILD→BACKTEST→DEPLOY 三步 + 三大特點）。自帶語系載入；桌機才顯示（呼叫端用 display 控制）。
import { useEffect, useContext, useMemo, Fragment } from "react";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS, TYPO, FONT } from "theme/tokens";

// ── 三步驟品牌圖示（橘色線稿，對齊設計稿）──
const IconBuild = () => (
  <svg width="34" height="34" viewBox="0 0 40 40" fill="none" stroke={COLORS.main} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="7" width="30" height="26" rx="4.5" />
    <line x1="5" y1="14.5" x2="35" y2="14.5" />
    <circle cx="9.5" cy="10.8" r="1" fill={COLORS.main} stroke="none" />
    <circle cx="13" cy="10.8" r="1" fill={COLORS.main} stroke="none" />
    <polyline points="17,20.5 13,24.5 17,28.5" />
    <polyline points="23,20.5 27,24.5 23,28.5" />
    <line x1="21.4" y1="19.5" x2="18.6" y2="29.5" />
  </svg>
);
const IconBacktest = () => (
  <svg width="34" height="34" viewBox="0 0 40 40" fill="none" stroke={COLORS.main} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <line x1="6" y1="33" x2="34" y2="33" opacity="0.35" />
    <polyline points="6,27 15,18.5 22,23.5 33,11" />
    <polyline points="26.5,11 33,11 33,17.5" />
  </svg>
);
const IconDeploy = () => (
  <svg width="34" height="34" viewBox="0 0 40 40" fill="none" stroke={COLORS.main} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4.5" y="8" width="31" height="20" rx="3.5" />
    <line x1="20" y1="28" x2="20" y2="32.5" />
    <line x1="15" y1="32.5" x2="25" y2="32.5" />
    <text x="20" y="21.6" fontSize="9.5" fontWeight="800" fill={COLORS.main} stroke="none" textAnchor="middle" fontFamily="Outfit, sans-serif">MT5</text>
  </svg>
);

export default function AuthPromoPanel() {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("AuthPromoPanel", "authentication/AuthPromoPanel/AuthPromoPanel_lang");
  }, [language, loadLanguageFile]);
  const L = useMemo(
    () => componentLanguageData.AuthPromoPanel?.Main || {},
    [componentLanguageData]
  );

  const logoSrc =
    language === "zh-TW" || language === "zh-CN"
      ? "/images/logo/logo_1_1.png"
      : "/images/logo/logo_1_2.png";

  // BUILD / BACKTEST / DEPLOY 為流程英文標籤（設計稿中英文皆英文）
  const steps = [
    { key: "BUILD", Svg: IconBuild, sub: L.step1Sub || "自然語言描述交易規則" },
    { key: "BACKTEST", Svg: IconBacktest, sub: L.step2Sub || "歷史資料驗證策略與風險" },
    { key: "DEPLOY", Svg: IconDeploy, sub: L.step3Sub || "一鍵部署至 MT5 自動執行策略" },
  ];
  const feats = [
    { icon: "verified_user", title: L.feat1Title || "無需寫程式", sub: L.feat1Sub || "自然語言即可建立" },
    { icon: "cloud", title: L.feat2Title || "雲端運算", sub: L.feat2Sub || "快速回測與最佳化" },
    { icon: "headset_mic", title: L.feat3Title || "專業支援", sub: L.feat3Sub || "協助你的量化旅程" },
  ];

  return (
    <MDBox
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        px: { md: 4, lg: 5 },
        py: 4,
        boxSizing: "border-box",
        overflow: "hidden",
        background: "linear-gradient(150deg, #FFE7D2 0%, #FFF3E8 42%, #FFFBF7 100%)",
      }}
    >
      {/* 淡 K 線浮水印（背景裝飾）*/}
      <MDBox
        component="svg"
        viewBox="0 0 200 260"
        sx={{ position: "absolute", left: -10, bottom: -10, width: 220, height: 286, opacity: 0.06, pointerEvents: "none" }}
      >
        {[
          [18, 60, 150, 40, 120],
          [46, 90, 210, 70, 150],
          [74, 40, 170, 30, 130],
          [102, 100, 230, 80, 180],
          [130, 70, 200, 55, 150],
          [158, 110, 240, 90, 200],
        ].map(([x, hi, lo, oT, oB], i) => (
          <g key={i} stroke={COLORS.main} strokeWidth="4" fill={COLORS.main}>
            <line x1={x} y1={hi} x2={x} y2={lo} />
            <rect x={x - 8} y={Math.min(oT, oB)} width="16" height={Math.abs(oB - oT)} />
          </g>
        ))}
      </MDBox>

      {/* 內容層 */}
      <MDBox sx={{ position: "relative", zIndex: 1 }}>
        {/* logo（橫式含字）*/}
        <MDBox
          component="img"
          src={logoSrc}
          alt="MASQuant"
          sx={{ height: 52, objectFit: "contain", display: "block", mb: 3.5 }}
        />

        {/* 15 DAYS */}
        <MDBox sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <span
            style={{
              fontFamily: FONT.OUTFIT,
              fontSize: 92,
              fontWeight: 800,
              lineHeight: 1,
              background: COLORS.gradient,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            15
          </span>
          <span style={{ fontFamily: FONT.OUTFIT, fontSize: 28, fontWeight: 700, color: COLORS.main, letterSpacing: 1 }}>
            {L.days || "DAYS"}
          </span>
        </MDBox>
        <p style={{ fontFamily: FONT.NOTO, fontSize: 46, fontWeight: 700, color: COLORS.black, lineHeight: 1.1, margin: "2px 0 12px" }}>
          {L.trialTitle || "免費使用"}
        </p>
        <p style={{ ...TYPO.bodyM, color: COLORS.grayDark, margin: 0, maxWidth: 340 }}>
          {L.trialSub || "註冊即開通 15 天完整功能，不需綁定信用卡。"}
        </p>

        <div style={{ height: 1, background: COLORS.grayLighter, margin: "26px 0" }} />

        {/* 從交易想法到 MT5 策略 */}
        <p style={{ ...TYPO.headingM, color: COLORS.black, margin: "0 0 6px" }}>
          {L.flowTitle || "從交易想法到 MT5 策略"}
        </p>
        <p style={{ ...TYPO.bodyM, color: COLORS.grayDark, margin: "0 0 22px" }}>
          {L.flowSub || "從策略建立、歷史回測到 MT5 部署，一次走完整個流程。"}
        </p>

        {/* 三步驟：BUILD → BACKTEST → DEPLOY */}
        <MDBox sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 3 }}>
          {steps.map((s, i) => (
            <Fragment key={s.key}>
              <MDBox sx={{ flex: 1, textAlign: "center", minWidth: 0 }}>
                <MDBox
                  sx={{
                    width: 62,
                    height: 62,
                    mx: "auto",
                    mb: 1.25,
                    borderRadius: "16px",
                    background: COLORS.white,
                    boxShadow: "0 8px 18px rgba(243,109,38,0.16)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <s.Svg />
                </MDBox>
                <p style={{ fontFamily: FONT.OUTFIT, fontSize: 13, fontWeight: 700, letterSpacing: 0.5, color: COLORS.black, margin: "0 0 4px", whiteSpace: "nowrap" }}>
                  {s.key}
                </p>
                <p style={{ ...TYPO.bodyS, color: COLORS.grayDark, margin: 0, lineHeight: 1.4 }}>{s.sub}</p>
              </MDBox>
              {i < steps.length - 1 && (
                <Icon sx={{ color: COLORS.grayMedian, mt: "20px", fontSize: "18px !important", flexShrink: 0 }}>
                  arrow_forward
                </Icon>
              )}
            </Fragment>
          ))}
        </MDBox>

        {/* 三大特點 */}
        <MDBox sx={{ display: "flex", gap: 1.5 }}>
          {feats.map((f) => (
            <MDBox key={f.title} sx={{ flex: 1, display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
              <Icon sx={{ color: COLORS.main, fontSize: "22px !important", flexShrink: 0, mt: "1px" }}>{f.icon}</Icon>
              <MDBox sx={{ minWidth: 0 }}>
                <p style={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 700, margin: "0 0 2px", whiteSpace: "nowrap" }}>{f.title}</p>
                <p style={{ fontFamily: FONT.NOTO, fontSize: 12, color: COLORS.grayDark, margin: 0, lineHeight: 1.35 }}>{f.sub}</p>
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
